import type { Breadcrumb, Event, EventHint } from "@sentry/nextjs"
import { ClientError } from "graphql-request"

import { withoutRequest } from "./errors"

// What WM sends to Sentry. The API reports every error it answers (and CloudWatch logs it in
// full), so WM reports only what the API can't see: its own failures, our API not answering, and
// Stripe. See "The rule" in docs/plans/sentry-cleanup.md.

const apiHost = (() => {
  try {
    return new URL(process.env.NEXT_PUBLIC_API_URL ?? "").host
  } catch {
    return ""
  }
})()

// Query strings and fragments carry Stripe client secrets, invite codes and referrer ids, and
// /invite/<code> is a guest-list code.
const scrubUrl = (url: string) => url.split(/[?#]/)[0].replace(/\/invite\/[^/]+/, "/invite/[code]")

// Headers that hold the visitor's IP address and location (server events).
const PRIVATE_HEADERS =
  /^(forwarded|x-forwarded-for|x-real-ip|x-vercel-forwarded-for|x-vercel-ip-)/i

const scrubRequest = (event: Event) => {
  const { request } = event
  if (!request) return
  if (request.url) request.url = scrubUrl(request.url)
  delete request.query_string
  if (request.headers) {
    for (const name of Object.keys(request.headers)) {
      if (PRIVATE_HEADERS.test(name)) delete request.headers[name]
      else if (/^referer$/i.test(name)) request.headers[name] = scrubUrl(request.headers[name])
    }
  }
}

/**
 * Shared by the browser and the server. Returns null for API errors the API already reported;
 * otherwise strips the request graphql-request puts into its messages, and private URL parts.
 */
export const beforeSend = (event: Event, hint: EventHint): Event | null => {
  // graphql-request's error carries the API's answer and the request, variables included.
  const clientError = hint.originalException instanceof ClientError ? hint.originalException : null
  if (clientError?.response.errors?.length) return null

  for (const value of event.exception?.values ?? []) {
    if (value.value) value.value = withoutRequest(value.value)
  }
  if (event.message) event.message = withoutRequest(event.message)

  // The API didn't answer with GraphQL (a gateway timeout, a 5xx): one issue per status.
  if (clientError) {
    event.fingerprint = ["api-error-status", String(clientError.response.status)]
  }

  scrubRequest(event)
  return event
}

const NETWORK_MESSAGES = [
  "Failed to fetch",
  "Load failed",
  "NetworkError",
  "Network request failed",
]

const isNetworkError = (error: unknown) =>
  error instanceof Error &&
  (error.name === "AbortError" ||
    NETWORK_MESSAGES.some((message) => error.message.includes(message)))

// Hosts of the fetches that failed in the 2 seconds before the event.
const failedHosts = (event: Event) => {
  const since = (event.timestamp ?? Date.now() / 1000) - 2
  const hosts = new Set<string>()
  for (const crumb of event.breadcrumbs ?? []) {
    if (crumb.category !== "fetch" || crumb.level !== "error") continue
    if ((crumb.timestamp ?? 0) < since) continue
    try {
      hosts.add(new URL(String(crumb.data?.url), window.location.href).host)
    } catch {
      // not a URL
    }
  }
  return hosts
}

/**
 * A request that never finished. Almost always the member left the page, went offline or closed
 * an in-app browser, and nobody can act on that. Only our API failing on its own is reported, as
 * one info-level issue: a spike there means members can't reach the API.
 */
const toApiUnreachable = (event: Event): Event | null => {
  const values = event.exception?.values ?? []
  const last = values[values.length - 1]
  if (last?.mechanism?.handled === false) return null
  if (document.visibilityState === "hidden") return null
  const hosts = failedHosts(event)
  if (hosts.size !== 1 || !hosts.has(apiHost)) return null

  event.level = "info"
  event.fingerprint = ["api-unreachable"]
  last.type = "API unreachable"
  return event
}

const isStripeHost = (host: string) => /(^|\.)stripe\.(com|network)$/.test(host)

/**
 * Stripe.js loads as checkout opens. A page left at that moment (an in-app browser reloading it,
 * a member going back) cuts off every request still running, the Stripe.js load with them, so
 * "Stripe unreachable" is reported only when no other host failed.
 */
const isPageLeft = (event: Event) =>
  document.visibilityState === "hidden" ||
  Array.from(failedHosts(event)).some((host) => !isStripeHost(host))

// Errors Sentry catches by itself: window.onerror, unhandled rejections, and its wrapper around
// listeners and timers. Errors our code captures go through processException instead.
const CAUGHT_BY_SENTRY = new Set(["onerror", "onunhandledrejection", "instrument"])
const NOT_A_FILE = new Set(["", "<anonymous>", "[native code]", "undefined"])

/**
 * In-app browsers (Instagram, Snapchat, Chrome for iOS), extensions and link scanners inject
 * scripts that throw on our pages. WM's code is served from /_next/, so an error Sentry caught by
 * itself whose throwing frame is elsewhere, or that has no frame at all, isn't ours.
 */
const isNotOurScript = (event: Event) => {
  const values = event.exception?.values ?? []
  const last = values[values.length - 1]
  if (!last || !CAUGHT_BY_SENTRY.has(last.mechanism?.type ?? "")) return false
  // Stripe.js throws IntegrationError only for mistakes in our integration.
  if (last.type === "IntegrationError") return false
  const throwingFrame = [...(last.stacktrace?.frames ?? [])]
    .reverse()
    .find((frame) => !NOT_A_FILE.has(frame.filename ?? ""))
  return !throwingFrame?.filename?.includes("/_next/")
}

export const beforeSendBrowser = (event: Event, hint: EventHint): Event | null => {
  // Automated browsers: E2E runs on previews and staging.
  if (navigator.webdriver) return null
  if (isNotOurScript(event)) return null

  const cleaned = beforeSend(event, hint)
  if (!cleaned) return null
  // processException in errors.ts sets this fingerprint.
  if (cleaned.fingerprint?.includes("stripe-unreachable") && isPageLeft(cleaned)) return null
  return isNetworkError(hint.originalException) ? toApiUnreachable(cleaned) : cleaned
}

// Tracing sends the same URLs: the page's request, every span's URL and, for App Router pages,
// the path as the transaction name (/invite/<code>).
export const beforeSendTransaction = (event: Event): Event => {
  scrubRequest(event)
  if (event.transaction) event.transaction = scrubUrl(event.transaction)
  for (const span of event.spans ?? []) {
    if (typeof span.data?.url === "string") span.data.url = scrubUrl(span.data.url)
    span.description = span.description?.replace(/(?:https?:\/\/|\/)\S+/g, scrubUrl)
  }
  return event
}

export const scrubBreadcrumb = (crumb: Breadcrumb): Breadcrumb => {
  if (crumb.category === "console") {
    // The logged values themselves can be errors holding a request with what the member typed.
    if (crumb.message) crumb.message = withoutRequest(crumb.message)
    if (crumb.data) delete crumb.data.arguments
  }
  if (crumb.category === "navigation" && crumb.data) {
    if (typeof crumb.data.from === "string") crumb.data.from = scrubUrl(crumb.data.from)
    if (typeof crumb.data.to === "string") crumb.data.to = scrubUrl(crumb.data.to)
  }
  if ((crumb.category === "fetch" || crumb.category === "xhr") && crumb.data?.url) {
    crumb.data.url = scrubUrl(String(crumb.data.url))
  }
  return crumb
}
